import React, { FC, ReactNode } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Button from '../Button/Button';
import Input from '../Input/Input';
import Typography from '../Typography/Typography';

type PortalHeaderProps = {
  dataTestId?: string;
  title: string;
  subTitle?: string;
  onPrimaryButtonClick?: () => void;
  onSecondaryButtonClick?: () => void;
  isSearchVisible?: boolean;
  isCreateButton?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonIcon?: ReactNode;
  secondaryButtonIcon?: ReactNode;
  primaryButtonVariant?: 'default' | 'outline' | 'secondary' | 'ghost';
  secondaryButtonVariant?: 'default' | 'outline' | 'secondary' | 'ghost';
  primaryButtonState?: {
    disabled?: boolean;
    hidden?: boolean;
    isLoading?: boolean;
  };
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
};

const PortalHeader: FC<PortalHeaderProps> = ({
  dataTestId,
  title,
  subTitle,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  isSearchVisible,
  isCreateButton,
  primaryButtonText,
  secondaryButtonText,
  primaryButtonIcon,
  secondaryButtonIcon,
  primaryButtonVariant,
  secondaryButtonVariant,
  primaryButtonState = {
    disabled: false,
    hidden: false,
    isLoading: false
  },
  onSearchChange,
  className
}) => {
  return (
    <div className={className ?? "px-7 pt-8 mb-5"} data-testid={dataTestId}>
      <div className="flex justify-between items-center min-h-10">
        <div className="flex flex-col gap-1">
          <Typography className="text-hd-h3">{title}</Typography>
          {subTitle && (
            <Typography
              className="text-sm text-md font-normal"
              style={{ color: 'var(--base-muted-foreground)' }}
            >
              {subTitle}
            </Typography>
          )}
        </div>
        <div className="flex items-center gap-2.5">
          {isSearchVisible && (
            <Input
              rightIconStyles="w-[15px] h-[15px] text-[15px]"
              rightIcon={
                <FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />
              }
              placeholder="Search"
              className="w-[300px]"
              onChange={onSearchChange}
              isHorizontal
              data-testid="PortalHeader-SearchInput"
            />
          )}
          {secondaryButtonText && (
            <Button
              data-testid="PortalHeader-SecondaryButton"
              onClick={onSecondaryButtonClick}
              leftIcon={secondaryButtonIcon}
              variant={secondaryButtonVariant}
            >
              {secondaryButtonText}
            </Button>
          )}
          {primaryButtonText && (
            <Button
              data-testid="PortalHeader-PrimaryButton"
              disabled={primaryButtonState?.disabled}
              isLoading={primaryButtonState?.isLoading}
              hidden={primaryButtonState?.hidden}
              isCreateButton={isCreateButton}
              onClick={onPrimaryButtonClick}
              leftIcon={primaryButtonIcon}
              variant={primaryButtonVariant}
            >
              {primaryButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalHeader;
